import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    forwardRef,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {BehaviorSubject} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, map, takeUntil} from 'rxjs/operators';
import {Unsubscribable} from '@core/interfaces/unsubscribable';

@Component({
    selector: 'ngx-input-with-search-box-form',
    templateUrl: './input-with-search-box-form.component.html',
    styleUrls: ['./input-with-search-box-form.component.scss', '../../selections/field-selection.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputWithSearchBoxFormComponent),
            multi: true,
        },
    ],
})
//todo: currently only support multiple selection.
export class InputWithSearchBoxFormComponent extends Unsubscribable implements OnInit, OnChanges {
    @Input() options: {value: string; count: number}[] = [];
    @Input() selectLabelLength: number = 0;
    @Input() formValueControl: FormControl;
    @Input() nameFieldSearchControl: FormControl;
    @Input() withStopPropagation?: boolean = false;
    @Input() placeholder?: string = 'Select';
    selectedOptions: {value: string; count: number}[] = [];
    optionSource: {value: string; count: number}[] = [];

    constructor() {
        super();
    }

    filteredOptions: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    selectLabel: string = '';
    customSelectLabel: boolean = false;
    @ViewChild('filterInput') filterInput: ElementRef;

    ngOnInit(): void {
        this.customSelectLabel = this.selectLabelLength > 0;
        if (this.customSelectLabel) {
            this.selectLabel = this.getSelectedLabel(this.formValueControl.value, this.selectLabelLength);
        }
        // Update filtered options based on search input
        this.formValueControl.valueChanges
            .pipe(
                takeUntil(this.unsubscribe$),
                distinctUntilChanged(),
                filter((value) => {
                    let isSearch = value?.includes('');
                    if (isSearch) {
                        let _value = value.filter((item) => item !== '');
                        this.formValueControl.setValue(_value, {emitEvent: false});
                    }

                    return !isSearch;
                }),
                map((value: string[]) => {
                    this.selectedOptions = this.selectedOptions.filter((item) => value.includes(item.value));
                    this.options.forEach((item) => {
                        const alreadySelected = this.selectedOptions.some((selected) => selected.value === item.value);
                        if (value.includes(item.value) && !alreadySelected) {
                            this.selectedOptions.push(item);
                        }
                    });
                    this.selectLabel = this.getSelectedLabel(value, this.selectLabelLength);

                    this.moveSelectedToTop(value, this.options);
                    return value;
                }),
                debounceTime(500),
            )
            .subscribe();
    }

    clearFilter(event): void {
        event.stopPropagation();
        this.nameFieldSearchControl.reset();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const needsMerge = this.selectedOptions.some((so) => !this.options.some((o) => o.value === so.value));
        const mergedOption = needsMerge
            ? [...this.selectedOptions, ...this.options].filter(
                  (item, index, self) => index === self.findIndex((i) => i.value === item.value),
              )
            : this.options;
        this.optionSource = mergedOption.sort();
        this.filteredOptions.next(mergedOption.map((item) => item.value));
        const selectedValues: string[] = this.selectedOptions.map((opt) => opt.value);
        this.moveSelectedToTop(selectedValues, mergedOption);
    }

    moveSelectedToTop(selectedValues: string[], allOptions) {
        const selected = allOptions.filter((opt) => selectedValues.includes(opt.value));

        const unselected = allOptions.filter((opt) => !selectedValues.includes(opt.value));

        const mergedOption = [...selected, ...unselected];
        this.filteredOptions.next(mergedOption.map((item) => item.value));
    }

    getSelectedLabel(data: string[], maxLength: number = 15): string {
        if (!data || data.length === 0) return '';

        let text = data.join(', ');
        if (text.length > maxLength) {
            text = text.slice(0, maxLength) + '...';
        }

        return text;
    }

    getCount(option) {
        const options = this.optionSource;
        return options.find((item) => item.value === option).count || '';
    }
}
